// Shopify embedded-context detection and persistence.
//
// TRD: docs/product/features/completed/shopify-embedded-install-auth/trd.md
// Bounded Unit B (cq-app), B1/B6.
//
// Managed installation lands the app in Shopify's iframe with
// `?embedded=1&host=<base64>&shop=<shop>.myshopify.com&id_token=...` on the
// FIRST load only -- every subsequent client-side (React Router) navigation
// within the same tab has none of these query params, since they're not
// part of our own route paths. sessionStorage is the persistence mechanism
// (not Redux) deliberately: this is read by plain utility functions
// (api_manager.js's interceptor, App.js's pre-render guard) that need a
// synchronous, store-independent answer before any component has mounted,
// mirroring the existing `currentProjectService.js` sessionStorage
// precedent for "must survive a full navigation within the tab" state.
const STORAGE_KEY = 'cm_shopify_embedded_context';

/**
 * Parses `embedded`/`host`/`shop` from a location.search string. Returns
 * null unless all three are present -- a partial match (e.g. just `?shop=`
 * on some stale/malformed link) is deliberately NOT treated as a valid
 * embedded load; see B4.2's guard for how a bare `shop` param alone is
 * still used as a *weaker* redirect signal, kept separate from this
 * stricter "is this genuinely an embedded App Bridge load" check.
 */
export function parseShopifyEmbeddedParams(search) {
  const params = new URLSearchParams(search);
  const embedded = params.get('embedded');
  const host = params.get('host');
  const shop = params.get('shop');
  const idToken = params.get('id_token');
  if (embedded === '1' && host && shop) {
    return { embedded, host, shop, idToken: idToken || null };
  }
  return null;
}

function readStore() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    // sessionStorage unavailable (private-browsing edge cases, storage
    // quota, etc.) -- non-fatal, just means embedded state can't persist
    // across a reload. Caller-visible effect is degrading to "standalone".
    return null;
  }
}

function writeStore(value) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (e) {
    // Same non-fatal rationale as readStore.
  }
}

/**
 * Call once, at bootstrap, with the INITIAL location.search. If it carries
 * valid embedded params, persists { shop, host } to sessionStorage (without
 * clobbering an already-known isShopifyAccountOwner/projectId set by a
 * prior call in this same tab session) and returns the parsed params.
 * Returns null (and persists nothing) for a non-embedded load.
 */
export function establishShopifyEmbeddedContext(search) {
  const parsed = parseShopifyEmbeddedParams(search);
  if (!parsed) return null;
  const existing = readStore() || {};
  writeStore({ ...existing, shop: parsed.shop, host: parsed.host });
  return parsed;
}

/** True if this tab has ever established a valid embedded context. */
export function isShopifyEmbeddedContext() {
  return !!readStore();
}

export function getShopifyContext() {
  return readStore();
}

export function setShopifyProjectId(projectId) {
  const existing = readStore();
  if (!existing) return;
  writeStore({ ...existing, projectId });
}

export function getShopifyProjectId() {
  const ctx = readStore();
  return ctx ? ctx.projectId || null : null;
}

// B6: isShopifyAccountOwner. Field name CONFIRMED against cq-api's shipped
// A1 (`POST /auth/shopify/session-exchange`) response, reconciliation
// follow-up from the coordinator: `is_shopify_account_owner` (snake_case
// only -- matches `project_id`/`is_new_install`'s casing already in that
// same `data` object), present in both the fresh-install and re-entry
// branches. Important nuance from cq-api: this can be `false` even when a
// CliqMetrics cookie IS issued -- cookie issuance only means "tracked
// installer," ownership additionally requires Shopify's `account_owner`
// flag at install time -- so B6 gates strictly on this field, never on
// "a session/cookie exists." Defaults to `false` (fail-closed -- hides
// owner-only controls) when the field is absent/not `true`.
export function setShopifyAccountOwnerFromResponse(responseData) {
  const existing = readStore();
  if (!existing) return;
  const value = !!(responseData && responseData.is_shopify_account_owner === true);
  writeStore({ ...existing, isShopifyAccountOwner: value });
}

export function getIsShopifyAccountOwner() {
  const ctx = readStore();
  return !!(ctx && ctx.isShopifyAccountOwner);
}

export function clearShopifyEmbeddedContext() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // non-fatal
  }
}
